import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { LinkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { healthConnectSupported, syncHealthConnect } from '../../utils/healthConnect';

/**
 * Health Connect device row for MePage's "Connected Devices" card.
 *
 * Android-native only (renders nothing on web/iOS). Health Connect is a HUB, not a
 * single device: many apps write into it — Garmin, Samsung Health / Galaxy Watch,
 * Fitbit, Oura, WHOOP, Coros — plus the phone's own sensors. There's no OAuth
 * "connect": tapping Sync requests the on-device permission, reads the supported
 * metrics from whatever's in Health Connect, and POSTs them to the backend. The
 * how-to covers the universal pattern (turn on Health Connect sharing in each source
 * app) with the menu path for the common ones. Apple Health is NOT here — it's iOS
 * and doesn't bridge to Health Connect.
 */
export default function HealthConnectRow() {
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  if (!healthConnectSupported()) return null;

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncHealthConnect({ days: 30 });
      setLastResult(result);
      if (result.note && result.written === 0) {
        toast(result.note, { icon: 'ℹ️' });
      } else {
        toast.success(`Synced ${result.written} record${result.written === 1 ? '' : 's'} from Health Connect`);
      }
    } catch (err) {
      toast.error(err?.message || 'Health Connect sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const connected = lastResult && lastResult.written > 0;

  return (
    <div className="p-4 mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {connected ? (
              <CheckCircleIcon className="w-6 h-6 text-green-500" />
            ) : (
              <LinkIcon className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Health Connect</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {connected
                ? `Last sync: ${lastResult.written} record${lastResult.written === 1 ? '' : 's'}`
                : 'Garmin, Galaxy Watch & other Android health apps'}
            </p>
          </div>
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync'}
          </Button>
        </div>
      </div>

      {/* Health Connect only holds what each source app writes into it. Spell out the
          universal step (turn on Health Connect sharing in the app) plus the menu path
          for the common ones, so "Sync" actually has data to pull. */}
      <button
        type="button"
        onClick={() => setShowHelp((v) => !v)}
        className="mt-3 flex items-center gap-1 text-sm font-medium text-amber-600 dark:text-amber-400"
      >
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${showHelp ? 'rotate-180' : ''}`} />
        How to connect your device
      </button>
      {showHelp && (
        <div className="mt-2 ml-1 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          <p>
            Health Connect only holds what each app puts in it, so turn on
            <span className="font-medium"> Health Connect sharing</span> in the app your
            device uses, then tap <span className="font-medium">Sync</span>:
          </p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li><span className="font-medium">Garmin</span> — Garmin Connect → More → Settings → Health Connect</li>
            <li><span className="font-medium">Samsung / Galaxy Watch</span> — Samsung Health → Settings → Health Connect</li>
            <li><span className="font-medium">WHOOP, Coros & others</span> — same idea: find Health Connect in the app’s settings</li>
          </ul>
          <p>Allow every data type, wear the device (snug, heart-rate on), and let its app sync. Steps, sleep, heart rate and workouts arrive within a few minutes.</p>
          <p className="text-gray-500 dark:text-gray-400">Notes: <span className="font-medium">Fitbit</span> and <span className="font-medium">Oura</span> connect directly above — they don’t need Health Connect. <span className="font-medium">Apple Watch</span> can’t use it at all (it’s iOS — an iOS app is coming). And brand-only metrics (Garmin Body Battery/Stress, etc.) aren’t Health Connect data types, so they don’t cross over.</p>
        </div>
      )}
    </div>
  );
}
